import * as React from 'react';
import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { HomeCustomer } from './HomeCustomer';
import { OCard } from '../molecules';

export const Home: FC = () => {
    const navigate = useNavigate();
    let auth = useAuth();
    useEffect(() => {
        if (auth.user?.profile.role === 'employee') {
            setTimeout(() => { // Use timeout the give time to update the redux store.
                navigate('/employee');
            }, 10);
        }
    }, [auth.user]);

    return (
        <main>
            {/* Hero Section */}
            <section id="home" className="relative bg-blue-900 text-white">
                <div className="absolute inset-0 overflow-hidden">
                    {/* Hero background image - middle-aged couple planning retirement */}
                    <div className="absolute inset-0 bg-black opacity-50"></div>
                    <img
                        src="/images/people.jpg"
                        alt="Middle-aged couple planning retirement"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="container mx-auto px-4 py-24 relative z-10">
                    <div className="max-w-2xl">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6">Uw toekomst, onze zorg</h1>
                        <p className="text-xl mb-8">
                            Bij PensioenZeker zorgen wij voor een zorgeloze toekomst met betrouwbare en transparante pensioenoplossingen op maat.
                        </p>
                        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                            <a
                                href="#berekenen"
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg text-center transition duration-300"
                            >
                                Bereken Uw Pensioen
                            </a>
                            <a
                                href="#contact"
                                className="bg-white hover:bg-gray-100 text-blue-900 font-bold py-3 px-6 rounded-lg text-center transition duration-300"
                            >
                                Neem Contact Op
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-16 bg-gray-100">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">Onze Pensioenoplossingen</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Service 1 */}
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                            <div className="h-48 overflow-hidden">
                                <img
                                    src="/images/collectief-pensioen.svg"
                                    alt="Collectief Pensioen"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold mb-3">Collectief Pensioen</h3>
                                <p className="text-gray-700 mb-4">
                                    Complete pensioenoplossingen voor werkgevers die het beste willen voor hun werknemers.
                                </p>
                                <a
                                    href="#collectief"
                                    className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center"
                                >
                                    Meer informatie
                                    <svg className="w-4 h-4 ml-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd"></path>
                                    </svg>
                                </a>
                            </div>
                        </div>

                        {/* Service 2 */}
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                            <div className="h-48 overflow-hidden">
                                <img
                                    src="/images/individueel-pensioen.svg"
                                    alt="Individueel Pensioen"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold mb-3">Individueel Pensioen</h3>
                                <p className="text-gray-700 mb-4">
                                    Flexibele pensioenoplossingen voor ZZP'ers en particulieren die zelf willen bouwen aan hun toekomst.
                                </p>
                                <a
                                    href="#individueel"
                                    className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center"
                                >
                                    Meer informatie
                                    <svg className="w-4 h-4 ml-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd"></path>
                                    </svg>
                                </a>
                            </div>
                        </div>

                        {/* Service 3 */}
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                            <div className="h-48 overflow-hidden">
                                <img
                                    src="/images/pensioen-advies.svg"
                                    alt="Pensioen Advies"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold mb-3">Pensioen Advies</h3>
                                <p className="text-gray-700 mb-4">
                                    Persoonlijk advies van onze experts om uw pensioen optimaal af te stemmen op uw wensen en situatie.
                                </p>
                                <a
                                    href="#advies"
                                    className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center"
                                >
                                    Meer informatie
                                    <svg className="w-4 h-4 ml-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd"></path>
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">Wat Onze Klanten Zeggen</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Testimonial 1 */}
                          <div className="bg-gray-50 p-6 rounded-lg shadow">
                            <div className="flex items-center mb-4">
                                <img
                                    src="/images/testimonial-jan.svg"
                                    alt="Jan de Vries"
                                    className="w-12 h-12 rounded-full object-cover mr-4"
                                />
                                <div>
                                    <h4 className="font-bold">Jan de Vries</h4>
                                    <p className="text-sm text-gray-600">Ondernemer, 52 jaar</p>
                                </div>
                            </div>
                            <p className="text-gray-700">
                                "Dankzij PensioenZeker heb ik eindelijk inzicht in mijn pensioen. Hun adviseurs hebben mij geholpen met een plan dat perfect past bij mijn situatie als ondernemer."
                            </p>
                        </div>

                        {/* Testimonial 2 */}
                        <div className="bg-gray-50 p-6 rounded-lg shadow">
                            <div className="flex items-center mb-4">
                                <img
                                    src="/images/testimonial-marieke.svg"
                                    alt="Marieke Jansen"
                                    className="w-12 h-12 rounded-full object-cover mr-4"
                                />
                                <div>
                                    <h4 className="font-bold">Marieke Jansen</h4>
                                    <p className="text-sm text-gray-600">HR Manager, 48 jaar</p>
                                </div>
                            </div>
                            <p className="text-gray-700">
                                "Als HR manager was ik op zoek naar een betrouwbare pensioenuitvoerder voor ons bedrijf. PensioenZeker biedt niet alleen een goed product, maar ook uitstekende service voor onze medewerkers."
                            </p>
                        </div>

                        {/* Testimonial 3 */}
                        <div className="bg-gray-50 p-6 rounded-lg shadow">
                            <div className="flex items-center mb-4">
                                <img
                                    src="/images/testimonial-peter-annemieke.svg"
                                    alt="Peter en Annemieke de Boer"
                                    className="w-12 h-12 rounded-full object-cover mr-4"
                                />
                                <div>
                                    <h4 className="font-bold">Peter en Annemieke de Boer</h4>
                                    <p className="text-sm text-gray-600">Bijna met pensioen, 63 jaar</p>
                                </div>
                            </div>
                            <p className="text-gray-700">
                                "Nu we bijna met pensioen gaan, wilden we zekerheid over onze financiële toekomst. PensioenZeker heeft ons geholpen om alles op orde te krijgen voor een zorgeloze pensioentijd."
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-16 bg-blue-800 text-white relative">
                <div className="absolute inset-0 overflow-hidden">
                    {/*
            Background photorealistic image of middle-aged people having fun in a park with water
            This is a placeholder base64-encoded PNG image. In a real implementation, this would be replaced with:
            - A photorealistic PNG image (not SVG)
            - Showing middle-aged people having fun in a park with water
            - People only taking up 25% of the image
            - People spaced apart and not touching each other
            - Dimensions: 1200x600 pixels
          */}
                    <img
                        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg=="
                        alt="Middle-aged people having fun in a park with water"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="container mx-auto px-4 text-center relative z-10">
                    <h2 className="text-3xl font-bold mb-6">Klaar om uw pensioen te regelen?</h2>
                    <p className="text-xl mb-8 max-w-3xl mx-auto">
                        Neem vandaag nog contact met ons op voor een vrijblijvend gesprek over uw pensioenmogelijkheden.
                    </p>
                    <a
                        href="#contact"
                        className="bg-white hover:bg-gray-100 text-blue-900 font-bold py-3 px-8 rounded-lg inline-block transition duration-300"
                    >
                        Maak een Afspraak
                    </a>
                </div>
            </section>
        </main>
    );
}
